'use client';

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type Theme = 'sky' | 'orange' | 'green';

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const themeColors = {
  sky: {
    primary: '#0EA5E9', // sky-500
    primaryHover: '#0284C7', // sky-600
    primaryLight: '#E0F2FE', // sky-100
    primaryDark: '#0369A1', // sky-700
    gradientStart: '#0c4a6e',
    gradientMiddle: '#0369a1',
    gradientEnd: '#0ea5e9',
  },
  orange: {
    primary: '#FF7917',
    primaryHover: '#E66A00',
    primaryLight: '#FFF3E0',
    primaryDark: '#CC5500',
    gradientStart: '#FF7917', // deep orange
    gradientMiddle: '#FFA933', // mid orange
    gradientEnd: '#FED102', // light yellow
  },
  green: {
    primary: '#22C55E',        // green-500
    primaryHover: '#16A34A',   // green-600
    primaryLight: '#E6F4EA',   // light green background
    primaryDark: '#15803D',    // green-700
    gradientStart: '#14532D',  // dark green
    gradientMiddle: '#22C55E', // medium green
    gradientEnd: '#A7F3D0',    // light mint green
  }
};

// Cookie helper functions
const setCookie = (name: string, value: string, days: number = 365) => {
  if (typeof document === 'undefined') return;
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
};

const getCookie = (name: string): string | null => {
  if (typeof document === 'undefined') return null;
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return decodeURIComponent(parts.pop()?.split(';').shift() || '');
  }
  return null;
};

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>('orange');
  const [mounted, setMounted] = useState(false);

  // Load theme from cookies on mount
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setMounted(true);
    // const savedTheme = getCookie('petrochina-theme') as Theme;
    // if (savedTheme && (savedTheme === 'sky' || savedTheme === 'orange' || savedTheme === 'green')) {
    //   setTheme(savedTheme);
    // }
  }, []);

  // Update CSS variables when theme changes
  useEffect(() => {
    if (!mounted) return;
    
    const colors = themeColors[theme];
    const root = document.documentElement;
    
    root.style.setProperty('--color-primary', colors.primary);
    root.style.setProperty('--color-primary-hover', colors.primaryHover);
    root.style.setProperty('--color-primary-light', colors.primaryLight);
    root.style.setProperty('--color-primary-dark', colors.primaryDark);
    root.style.setProperty('--color-gradient-start', colors.gradientStart);
    root.style.setProperty('--color-gradient-middle', colors.gradientMiddle);
    root.style.setProperty('--color-gradient-end', colors.gradientEnd);
    
    // Save to cookies
    // setCookie('petrochina-theme', theme);
  }, [theme, mounted]);

  const toggleTheme = () => {
    setTheme(prev => {
      if (prev === 'sky') return 'orange';
      if (prev === 'orange') return 'green';
      return 'sky'; // if green or any other
    });
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

export { themeColors };
